import { InputAdornment, TextField, TextFieldProps } from "@mui/material";
import { useField } from "@unform/core";
import * as React from "react";
// O yup exporta AnySchema, mas não AnyObject.
import { number, string, AnySchema } from "yup";

// --- Tipagens ---

type TMaskFieldProps = TextFieldProps & {
	name: string;
	mask: string;
	trimEmptySpaces?: boolean;
	// NOVAS PROPS DE VALIDAÇÃO
	min?: number;
	max?: number;
};

// --- Funções de Máscara (INCLUÍDAS INTERNAMENTE) ---

const maskCPFT = (value: string) => {
	let cell = value
		.replace(/\D/g, "")
		.replace(/(\d{3})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d{1,2})/, "$1-$2")
		.replace(/(-\d{2})\d+?$/, "$1");
	if (cell.length < 1) {
		cell = "";
	}
	return cell?.trim();
};

const maskCNPJT = (value: string) => {
	let cell = value
		.replace(/\D/g, "")
		.replace(/(\d{2})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1.$2")
		.replace(/(\d{3})(\d)/, "$1/$2")
		.replace(/(\d{4})(\d)/, "$1-$2")
		.replace(/(-\d{2})\d+?$/, "$1");
	if (cell.length < 1) {
		cell = "";
	}
	return cell?.trim();
};

const maskInteiro = (value: string | number) => {
	if (typeof value === "number") {
		return value.toString();
	}
	let cell = String(value)
		.replace(/\D/g, "")
		.replace(/(\b[0-9]\b)/g, "$1");
	if (cell.length < 1) {
		cell.replace(/\D/g, "");
		cell = "";
	}
	return cell?.trim();
};

const maskCEP = (value: string) => {
	let cell = value
		.replace(/\D/g, "")
		.replace(/(\d{5})(\d)/, "$1-$2")
		.replace(/(-\d{3})\d+?$/, "$1");
	if (cell.length < 1) {
		cell = "";
	}
	return cell?.trim();
};

const maskPhone = (value: string) => {
	value = value.replace(/\D/g, "");
	let cell =
		value.length <= 10
			? value
					.replace(/\D/g, "")
					.replace(/(\d{2})(\d)/, "($1) $2")
					.replace(/(\d{4})(\d)/, "$1-$2")
					.replace(/(-\d{4})\d+?$/, "$1")
			: value
					.replace(/\D/g, "")
					.replace(/(\d{2})(\d)/, "($1) $2")
					.replace(/(\d{5})(\d)/, "$1-$2")
					.replace(/(-\d{4})\d+?$/, "$1");
	if (cell.length < 1) {
		cell = "";
	}
	return cell?.trim();
};

const maskCell = (value: string) => {
	let cell =
		value.length <= 14
			? value
					.replace(/\D/g, "")
					.replace(/(\d{2})(\d)/, "($1) $2")
					.replace(/(\d{4})(\d)/, "$1-$2")
					.replace(/(-\d{4})\d+?$/, "$1")
			: value
					.replace(/\D/g, "")
					.replace(/(\d{2})(\d)/, "($1) $2")
					.replace(/(\d{5})(\d)/, "$1-$2")
					.replace(/(-\d{4})\d+?$/, "$1");
	if (cell.length < 1) {
		cell = "";
	}
	return cell?.trim();
};

const maskMMYYYY = (value: string) => {
	let cell = value.replace(/\D/g, "").replace(/^(\d{2})(\d)/g, "$1/$2");
	if (cell.length < 1) {
		cell = "";
	}
	if (cell.length > 7) {
		cell = cell.substring(0, 7);
	}
	return cell?.trim();
};

const maskCpfOrCnpj = (value: string) => {
	const cleanValue = value.replace(/\D/g, "");
	if (cleanValue.length <= 11) {
		return maskCPFT(value);
	}
	return maskCNPJT(value);
};

// 0.000,00 - Máscara de Real (Com correção de digitação)
const maskReal = (value: string) => {
	if (!value) return "";

	let cell = String(value);

	cell = cell.replace(/[^\d,\.]/g, "");
	cell = cell.replace(/\./g, ",");

	let cleanDigits = cell.replace(/\D/g, "");

	if (cleanDigits.length < 1) return "";

	// Corrigido para garantir que valores curtos sejam tratados como centavos
	if (cleanDigits.length < 3) {
		while (cleanDigits.length < 3) {
			cleanDigits = "0" + cleanDigits;
		}
	}

	if (cleanDigits.length > 3) {
		cleanDigits = cleanDigits.replace(/^(0+)/, "");
	}

	let formatted = cleanDigits.replace(/(\d{0,2})$/, ",$1").replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");

	if (formatted.startsWith("0.") && formatted.length > 4) {
		formatted = formatted.substring(1);
	}

	if (formatted === "0,00" && !value) return "";

	return formatted.trim();
};

const maskPercent = (value: string) => {
	return maskReal(value);
};

// --- Componente MaskField (Renomeado) ---

export const MaskField: React.FC<TMaskFieldProps> = ({ name, mask, trimEmptySpaces, value: propValue, min, max, ...rest }) => {
	const { fieldName, registerField, defaultValue, error, clearError } = useField(name);

	// Função para aplicar a máscara
	const applyMask = (value: string) => {
		switch (mask) {
			case "cpf":
				return maskCPFT(value);
			case "cep":
				return maskCEP(value);
			case "phone":
				return maskPhone(value);
			case "cell":
				return maskCell(value);
			case "cnpj":
				return maskCNPJT(value);
			case "real":
				return maskReal(value);
			case "percent":
				return maskPercent(value);
			case "inteiro":
				return maskInteiro(value);
			case "mesano":
				return maskMMYYYY(value);
			case "cpfOrCnpj":
				return maskCpfOrCnpj(value);
			default:
				return trimEmptySpaces ? value.replace(/( )/g, "") : value;
		}
	};

	// 1. Inicializa o estado local
	const initialValue = propValue !== undefined ? String(propValue) : defaultValue;
	const [value, setValue] = React.useState(() => applyMask(initialValue || ""));

	// 2. Atualiza estado se a prop `value` mudar no pai
	React.useEffect(() => {
		if (propValue !== undefined) {
			setValue(applyMask(String(propValue)));
		}
	}, [propValue, mask]);

	// 3. Efeito para registro no VForm, defaultValue E VALIDAÇÃO
	React.useEffect(() => {
		if (defaultValue) {
			setValue(applyMask(defaultValue));
		}

		// --- LÓGICA DE VALIDAÇÃO (MIN/MAX) ---
		// Corrigido: Usando AnySchema para evitar o erro de tipagem.
		let schema: AnySchema<any> = string();
		let isNumberField = mask === "real" || mask === "percent" || mask === "inteiro";

		if (isNumberField) {
			// Inicializamos o numberSchema aqui.
			let numberSchema = number().typeError(error || "Valor inválido");

			if (min !== undefined) {
				// A chamada .min(limite, mensagem) exige que o limite seja fornecido
				numberSchema = numberSchema.min(min, `O valor mínimo é ${min}`);
			}

			if (max !== undefined) {
				// A chamada .max(limite, mensagem) exige que o limite seja fornecido
				numberSchema = numberSchema.max(max, `O valor máximo é ${max}`);
			}

			schema = numberSchema;
		} else {
			schema = string();
		}
		// --- FIM LÓGICA DE VALIDAÇÃO ---

		registerField({
			name: fieldName,
			// Retorna o valor LIMPO para a validação (como number/string)
			getValue: () => {
				if (mask === "real" || mask === "percent") {
					const clean = value.replace(/R\$/g, "").replace(/\./g, "").replace(",", ".").replace("%", "");
					return clean === "" ? undefined : parseFloat(clean);
				}
				if (mask === "inteiro") {
					const clean = value.replace(/\D/g, "");
					return clean === "" ? undefined : parseInt(clean);
				}
				// Para outros campos (CPF/CNPJ), retorna a string limpa
				return value.replace(/\D/g, "");
			},
			// A função setValue do VForm agora aplica a máscara
			setValue: (_, newValue) => {
				const maskedValue = applyMask(String(newValue || ""));
				setValue(maskedValue);
			},
			clearValue: () => setValue(""),
		});
	}, [registerField, fieldName, defaultValue, mask, value, min, max, error]);

	// Define o Adornment (Prefixo/Sufixo)
	let startAdornment = undefined;
	let endAdornment = undefined;

	if (mask === "real") {
		startAdornment = <InputAdornment position="start">R$</InputAdornment>;
	} else if (mask === "percent") {
		endAdornment = <InputAdornment position="end">%</InputAdornment>;
	}

	return (
		<TextField
			{...rest}
			error={!!error}
			helperText={error}
			value={value}
			onChange={(e) => {
				const masked = applyMask(e.target.value);
				setValue(masked);
				rest.onChange?.(e);
				error && clearError();
			}}
			onKeyDown={(e) => {
				rest.onKeyDown?.(e);
			}}
			size="small"
			InputProps={{
				startAdornment: startAdornment,
				endAdornment: endAdornment,
				...rest.InputProps,
			}}
		/>
	);
};
